import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import type { CSSProperties, DOMAttributes, ForwardRefRenderFunction, ReactElement, ReactNode, Ref } from "react";
import { forwardRef, useState } from "react";
import { type AriaTextFieldProps, mergeProps, useFocus, useTextField, VisuallyHidden } from "react-aria";

import { useDOMRef } from "../../hooks.ts";
import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import style from "./TextField.module.css";

type HTMLInputElementType = HTMLInputElement | HTMLTextAreaElement;

export interface TextFieldProps extends AriaTextFieldProps<HTMLInputElementType> {
  style?: CSSProperties;
  action?: ReactElement;
  extraAction?: ReactElement;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  onBaseChange?: DOMAttributes<HTMLInputElementType>["onChange"];
  /** To be used only when another "input" */
  noBorder?: boolean;
  // Specific to TextArea
  rows?: number;
  resize?: "none" | "both" | "horizontal" | "vertical";
}

const TextFieldRender: ForwardRefRenderFunction<HTMLInputElementType, TextFieldProps> = (props, ref) => {
  const inputRef = useDOMRef<HTMLInputElementType>(ref);
  const {
    label,
    placeholder,
    name,
    className,
    noBorder,
    icon,
    errorMessage,
    isInvalid,
    isDisabled,
    isReadOnly,
    isRequired,
    action,
    extraAction,
    onBaseChange,
    description,
    rows,
    resize = "both",
  } = props;

  const isTextArea = rows && rows > 1;

  const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField(
    { ...props, label: label || placeholder, inputElementType: isTextArea ? "textarea" : "input" },
    inputRef,
  );

  const [isFocus, onFocusChange] = useState(false);
  const { focusProps } = useFocus({ onFocusChange });

  const inputElementProps = mergeProps(inputProps, focusProps, { onChange: onBaseChange }, { style: { resize } });

  return (
    <div style={style} data-testid={`${name ? `${name}__` : ""}text-field`}>
      <div className={formStyle.input_layout}>
        {!!label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label {...labelProps}>{label}</Label>
            <Spacer />
            {isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
            {!isRequired && !isReadOnly && (
              <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
            )}
          </HStack>
        )}
        <Description
          {...descriptionProps}
          data-testid="text-field-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && (isReadOnly || !isRequired),
          })}
        >
          {description}
        </Description>
        <HStack
          spacing={3}
          className={clsx(style.input_group, formStyle.input_layout_column1, className, {
            [style.disabled]: isDisabled,
            [style.focus]: isFocus,
            [style.error]: isInvalid,
            [style.no_border]: noBorder,
          })}
          style={{ flex: 1 }}
        >
          {!label && (
            <VisuallyHidden>
              <label {...labelProps}>{placeholder}</label>
            </VisuallyHidden>
          )}
          {!!icon && <Icon icon={icon} className={style.icon} />}
          {isTextArea ? (
            <textarea
              className={style.textarea}
              {...inputElementProps}
              ref={inputRef as Ref<HTMLTextAreaElement>}
              rows={rows}
            />
          ) : (
            <input className={style.input} {...inputElementProps} ref={inputRef as Ref<HTMLInputElement>} />
          )}
          {!!action && !isDisabled && (
            <div className={style.action} data-testid="text-field-actions">
              {action}
            </div>
          )}
        </HStack>
        {!!extraAction && <HStack className={formStyle.input_layout_column2}>{extraAction}</HStack>}
        {!!errorMessage && isInvalid && (
          <div
            role="alert"
            {...errorMessageProps}
            className={clsx(formStyle.error_message, formStyle.input_layout_column1)}
            data-testid="number-field-error"
          >
            {errorMessage as ReactNode}
          </div>
        )}
      </div>
    </div>
  );
};

export const TextField = forwardRef(TextFieldRender);
